import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import LayoutContainer from 'components/Common/LayoutContainer'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import { useAppSelector } from 'hooks/reduxHooks'
import { isLEStaff } from 'selectors/accountSelectors'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import {
  LOYALTY_CREDIT_CARD_COLOUR_CREAM,
  LOYALTY_CREDIT_CARD_COLOUR_RUST,
  LOYALTY_CREDIT_CARD_LAYOUT_CONTAINER_CLASS,
} from './loyaltyCreditCardConstants'
import LoyaltyCreditCardApplyNowButton from './components/LoyaltyCreditCardApplyNowButton'
import { LOYALTY_CREDIT_CARD_APPLY_NOW_PLACEMENTS } from 'analytics/snowplow/events/loyaltyCreditCardEvents'
import { loyaltyCreditCardWrappingLabel } from './components/loyaltyCreditCardStyles'

const MaldivesWrapper = styled.div`
  background: white;

  ${mediaQueryUp.tablet} {
    padding: ${rem(64)} 0;
  }
`

const MaldivesImageFrame = styled.div`
  position: relative;
  background: #1a4a6e;
  margin: 0 ${rem(-20)};

  ${mediaQueryUp.tablet} {
    border-radius: ${rem(8)};
    overflow: hidden;
    min-height: ${rem(415)};
    margin: 0;

    &:not(:lang(en)) {
      display: flex;
      align-items: center;
    }
  }
`

const MaldivesBackgroundImage = styled.div`
  position: absolute;
  inset: 0;

  picture,
  > * {
    width: 100%;
    height: 100%;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }
`

const MaldivesContent = styled.div`
  position: relative;
  padding: ${rem(92)} ${rem(20)} ${rem(77)};
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  ${mediaQueryUp.tablet} {
    position: absolute;
    padding: 0;
    inset: ${rem(40)};

    &:not(:lang(en)) {
      position: relative;
      inset: auto;
      padding: ${rem(40)};
      width: 100%;
      box-sizing: border-box;
    }
  }
`

const MaldivesCard = styled.div`
  position: relative;
  background: white;
  border-radius: ${rem(8)};
  padding: ${rem(32)} ${rem(24)} ${rem(24)};
  width: 100%;
  max-width: ${rem(632)};
  margin: 0 auto;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
  align-items: center;

  ${mediaQueryUp.tablet} {
    padding: ${rem(40)};
  }
`

const LimitedBadge = styled.span`
  position: absolute;
  top: ${rem(-18)};
  inset-inline: 0;
  margin-inline: auto;
  width: fit-content;
  background: ${LOYALTY_CREDIT_CARD_COLOUR_RUST};
  color: ${LOYALTY_CREDIT_CARD_COLOUR_CREAM};
  font-size: ${rem(14)};
  font-weight: 600;
  padding: ${rem(6)} ${rem(16)};
  border-radius: ${rem(100)};
  white-space: nowrap;
`

const MaldivesApplyButton = styled(LoyaltyCreditCardApplyNowButton)`
  && {
    padding: ${rem(16)};

    ${loyaltyCreditCardWrappingLabel}
  }
`

function LoyaltyCreditCardMaldivesSection() {
  const intl = useIntl()
  const isStaff = useAppSelector(isLEStaff)

  if (isStaff) {
    return undefined
  }

  return (
    <MaldivesWrapper>
      <LayoutContainer className={LOYALTY_CREDIT_CARD_LAYOUT_CONTAINER_CLASS} variant="large">
        <MaldivesImageFrame>
          <MaldivesBackgroundImage>
            <ResponsiveImage
              id="societe-credit-card-win-a-trip"
              fit="center"
              objectFit="cover"
            />
          </MaldivesBackgroundImage>
          <MaldivesContent>
            <MaldivesCard>
              <LimitedBadge>
                {intl.formatMessage({
                  id: 'vNjFhVj4B2NTWwn3RIAi',
                  defaultMessage: 'Limited time only',
                  description: 'Limited time badge in the Maldives prize section on the Société Credit Card page',
                })}
              </LimitedBadge>
              <Heading variant="heading1">
                {intl.formatMessage(
                  {
                    id: '1JVWmKCbzqjpF70odoO0',
                    defaultMessage: 'Win a trip to the {italic}',
                    description: 'Maldives win section heading on Société Credit Card page',
                  },
                  {
                    italic: (
                      <em>
                        {intl.formatMessage({
                          id: '_hW3V4fPX_k74485Xx5g',
                          defaultMessage: 'Maldives',
                          description: 'Italic emphasis text in the Maldives prize heading on the Société Credit Card page',
                        })}
                      </em>
                    ),
                  },
                )}
              </Heading>
              <BodyText variant="large" align="center">
                {intl.formatMessage({
                  id: 'mK3hY0HmX8PAJsbCnxSN',
                  defaultMessage: "Approved applicants for the Société Credit Card will automatically be entered to win a 5-night Maldives escape to Outrigger Maldives Maafushivaru Resort for 2 people. The entries to the competition close 29 October. Be one of the first 100 approved cardholders and you'll also receive 25,000 bonus Société Points.",
                  description: 'Maldives win section body on Société Credit Card page',
                })}
              </BodyText>
              <MaldivesApplyButton fit="mobile-full-width" placement={LOYALTY_CREDIT_CARD_APPLY_NOW_PLACEMENTS.maldives}>
                {intl.formatMessage({
                  id: 'gl-WVuqd8KDHlXwNQo-g',
                  defaultMessage: 'Apply now to win',
                  description: 'Maldives win section CTA on Société Credit Card page',
                })}
              </MaldivesApplyButton>
            </MaldivesCard>
          </MaldivesContent>
        </MaldivesImageFrame>
      </LayoutContainer>
    </MaldivesWrapper>
  )
}

export default LoyaltyCreditCardMaldivesSection
